/**
 * Review carousel: shuffle pool, limit visible cards, desktop arrows + optional autoplay.
 */
class YorumsCarousel extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {HTMLButtonElement | null} */
  #prevBtn = null;

  /** @type {HTMLButtonElement | null} */
  #nextBtn = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number | null} */
  #autoplayId = null;

  /** @type {number | null} */
  #resumeId = null;

  /** @type {boolean} */
  #inViewport = false;

  /** @type {IntersectionObserver | null} */
  #viewportObserver = null;

  connectedCallback() {
    this.#bindElements();
    this.addEventListener('click', this.#onClick);
    this.#track?.addEventListener('scroll', this.#syncNav, { passive: true });
    this.#track?.addEventListener('pointerdown', this.#onUserInteract, { passive: true });
    this.#track?.addEventListener('touchstart', this.#onUserInteract, { passive: true });
    this.#track?.addEventListener('wheel', this.#onUserInteract, { passive: true });
    window.addEventListener('resize', this.#onResize);

    if (typeof ResizeObserver !== 'undefined') {
      this.#resizeObserver = new ResizeObserver(() => this.#syncNav());
      if (this.#track) this.#resizeObserver.observe(this.#track);
    }

    this.#observeViewport();

    this.#shuffleAndLimit();
    requestAnimationFrame(() => {
      this.#syncNav();
      this.#startAutoplay();
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#track?.removeEventListener('scroll', this.#syncNav);
    this.#track?.removeEventListener('pointerdown', this.#onUserInteract);
    this.#track?.removeEventListener('touchstart', this.#onUserInteract);
    this.#track?.removeEventListener('wheel', this.#onUserInteract);
    window.removeEventListener('resize', this.#onResize);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#viewportObserver?.disconnect();
    this.#viewportObserver = null;
    this.#stopAutoplay();
    if (this.#resumeId) window.clearTimeout(this.#resumeId);
  }

  #observeViewport() {
    if (typeof IntersectionObserver === 'undefined') {
      this.#inViewport = true;
      return;
    }

    this.#viewportObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        this.#inViewport = Boolean(entry?.isIntersecting);
        if (this.#inViewport) {
          this.#startAutoplay();
        } else {
          this.#stopAutoplay();
          if (this.#resumeId) {
            window.clearTimeout(this.#resumeId);
            this.#resumeId = null;
          }
        }
      },
      { threshold: 0.15 }
    );
    this.#viewportObserver.observe(this);
  }

  #bindElements() {
    this.#track = this.querySelector('[data-yorums-track]');
    this.#prevBtn = this.querySelector('[data-yorums-prev]');
    this.#nextBtn = this.querySelector('[data-yorums-next]');
  }

  #shuffleAndLimit() {
    this.#bindElements();
    const track = this.#track;
    if (!(track instanceof HTMLElement)) return;

    const items = Array.from(track.querySelectorAll('[data-yorums-item]'));
    if (!items.length) return;

    const limit = Math.max(1, Number.parseInt(this.dataset.showLimit || '10', 10) || 10);
    const inEditor = Boolean(window.Shopify?.designMode);

    // Keep stable order in the theme editor so blocks don't reshuffle on every remount.
    if (!inEditor) {
      for (let i = items.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        const tmp = items[i];
        items[i] = items[j];
        items[j] = tmp;
      }
    }

    items.forEach((item, index) => {
      track.appendChild(item);
      item.hidden = index >= limit;
    });

    if (!inEditor) track.scrollLeft = 0;
  }

  #onResize = () => {
    this.#syncNav();
    this.#restartAutoplay();
  };

  #onUserInteract = () => {
    this.#pauseAutoplayTemporarily();
  };

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const nav = event.target.closest('[data-yorums-prev], [data-yorums-next]');
    if (!(nav instanceof HTMLElement) || !this.contains(nav)) return;

    event.preventDefault();
    if (nav instanceof HTMLButtonElement && nav.disabled) return;
    this.#pauseAutoplayTemporarily();
    this.#scrollBy(nav.hasAttribute('data-yorums-prev') ? -1 : 1);
  };

  #autoplayEnabled() {
    // Autoplay + section remounts in the theme editor feel like a constant refresh.
    if (window.Shopify?.designMode) return false;
    return this.dataset.autoplay === 'true';
  }

  #autoplayMs() {
    const seconds = Number.parseFloat(this.dataset.autoplaySeconds || '3.5');
    return Math.max(2000, Math.round((Number.isFinite(seconds) ? seconds : 3.5) * 1000));
  }

  #startAutoplay() {
    this.#stopAutoplay();
    if (!this.#autoplayEnabled() || !this.#inViewport) return;

    this.#autoplayId = window.setInterval(() => {
      if (document.hidden || !this.#inViewport) return;
      this.#autoplayStep();
    }, this.#autoplayMs());
  }

  #stopAutoplay() {
    if (this.#autoplayId) {
      window.clearInterval(this.#autoplayId);
      this.#autoplayId = null;
    }
  }

  #restartAutoplay() {
    this.#stopAutoplay();
    this.#startAutoplay();
  }

  #pauseAutoplayTemporarily() {
    this.#stopAutoplay();
    if (this.#resumeId) window.clearTimeout(this.#resumeId);
    this.#resumeId = window.setTimeout(() => {
      this.#resumeId = null;
      if (this.#inViewport) this.#startAutoplay();
    }, this.#autoplayMs() * 2);
  }

  #autoplayStep() {
    const track = this.#track;
    if (!(track instanceof HTMLElement)) return;

    const max = this.#maxScrollLeft(track);
    if (max <= 4) return;

    if (track.scrollLeft >= max - 4) {
      track.scrollTo({ left: 0, behavior: 'smooth' });
      window.setTimeout(() => this.#syncNav(), 350);
      return;
    }

    this.#scrollBy(1);
  }

  /**
   * @returns {HTMLElement[]}
   */
  #visibleItems() {
    const track = this.#track;
    if (!(track instanceof HTMLElement)) return [];
    return Array.from(track.querySelectorAll('[data-yorums-item]')).filter(
      (el) => el instanceof HTMLElement && !el.hidden
    );
  }

  /**
   * @param {HTMLElement} track
   */
  #maxScrollLeft(track) {
    return Math.max(0, track.scrollWidth - track.clientWidth);
  }

  /**
   * @param {HTMLElement} track
   * @param {number} left
   */
  #scrollTrackTo(track, left) {
    const max = this.#maxScrollLeft(track);
    const next = Math.max(0, Math.min(max, Math.round(left)));
    track.scrollTo({ left: next, behavior: 'smooth' });
    window.requestAnimationFrame(() => this.#syncNav());
    window.setTimeout(() => this.#syncNav(), 350);
  }

  /**
   * Index of the card currently snapped / closest to the track's left edge.
   * @param {HTMLElement[]} items
   * @param {HTMLElement} track
   */
  #currentIndex(items, track) {
    const trackLeft = track.getBoundingClientRect().left;
    let best = 0;
    let bestDist = Infinity;

    items.forEach((item, index) => {
      const dist = Math.abs(item.getBoundingClientRect().left - trackLeft);
      if (dist < bestDist) {
        bestDist = dist;
        best = index;
      }
    });

    return best;
  }

  /**
   * Scroll exactly one card left/right.
   * @param {number} direction -1 = prev, 1 = next
   */
  #scrollBy(direction) {
    this.#bindElements();
    const track = this.#track;
    if (!(track instanceof HTMLElement)) return;

    const items = this.#visibleItems();
    if (!items.length) return;

    const current = this.#currentIndex(items, track);
    const nextIndex = Math.max(0, Math.min(items.length - 1, current + direction));
    const target = items[nextIndex];
    if (!(target instanceof HTMLElement)) return;

    const left = target.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
    this.#scrollTrackTo(track, left);
  }

  #syncNav = () => {
    this.#bindElements();
    const track = this.#track;
    if (!(track instanceof HTMLElement)) return;

    if (track.clientWidth < 8) {
      if (this.#prevBtn) this.#prevBtn.disabled = false;
      if (this.#nextBtn) this.#nextBtn.disabled = false;
      return;
    }

    const maxScroll = this.#maxScrollLeft(track);
    const scrollLeft = Math.abs(track.scrollLeft);
    const canScroll = maxScroll > 4;

    if (this.#prevBtn) this.#prevBtn.disabled = !canScroll || scrollLeft <= 2;
    if (this.#nextBtn) this.#nextBtn.disabled = !canScroll || scrollLeft >= maxScroll - 2;
  };
}

if (!customElements.get('yorums-carousel')) {
  customElements.define('yorums-carousel', YorumsCarousel);
}
